/**
 * One example set beside the generator's answer to the same brief.
 *
 * The verdict on what comes out proves nothing here — the search optimises exactly what the
 * verifier measures, so a perfect score is what it is *for*. The number that means something
 * is the drift: how far the rules, run forwards from the bare harmony and the written bass,
 * land from where Rimsky-Korsakov landed.
 */

import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import Board from "../components/Board";
import { ACTION } from "../components/Chrome";
import { Failed, Kicker, Loading } from "../components/Page";
import { label, rate, semitones } from "../lib/format";

function Row({ label, names }: { label: string; names: string[] }): JSX.Element {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-10 shrink-0 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-quiet">
        {label}
      </span>
      <span className="font-mono text-[15px] leading-relaxed">{names.join(" ")}</span>
    </div>
  );
}

export default function ComparePage(): JSX.Element {
  const { id = "" } = useParams();
  const { data, error, loading } = useAsync(`compare:${id}`, () => api.compare(id));

  const shell = (children: JSX.Element): JSX.Element => (
    <Board
      kicker={`Example ${id} · re-orchestrated`}
      title="Against the book"
      back={{ to: `/book/${id}`, label: "The verdict" }}
      measure="wide"
    >
      {children}
    </Board>
  );
  if (loading) return shell(<Loading what={`the re-orchestration of ${id}`} />);
  if (error !== null) return shell(<Failed error={error} />);
  if (data === null) return shell(<Failed error="that comparison came back empty" />);

  const row = data.comparison;
  const roster = [...new Set(row.instruments)].map(label).join(", ");

  return (
    <Board
      kicker={`Example ${row.example} · re-orchestrated`}
      title="Against the book"
      back={{ to: `/book/${row.example}`, label: "The verdict" }}
      lede={`Same brief, same players — ${roster} — and the bass Rimsky-Korsakov wrote. Everything above the bass is the generator's.`}
      actions={
        <Link to="/evidence" className={ACTION}>
          All three tests
        </Link>
      }
      measure="wide"
    >
      <div className="space-y-7">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["drift", semitones(row.drift), "semitones, mean, part for part"],
            ["identical", `${row.identical}/${row.compared}`, "chords note for note"],
            ["same pitches", `${row.samePitches}/${row.compared}`, "whoever plays them"],
            ["verdicts", `${rate(row.rk.score)} → ${rate(row.ours.score)}`, "his, then ours"],
          ].map(([label, value, note]) => (
            <div key={label} className="rounded-xl border border-rule bg-leaf p-4">
              <div className="font-mono text-[22px] leading-none">{value}</div>
              <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-quiet">
                {label}
              </div>
              <div className="mt-1 text-[11px] leading-snug text-ink-faded">{note}</div>
            </div>
          ))}
        </div>

        {row.pairs.length > 0 ? (
          <section className="space-y-2">
            <Kicker>Note for note</Kicker>
            <div className="grid gap-px overflow-hidden rounded-xl border border-rule bg-rule-faint lg:grid-cols-2">
              {row.pairs.map((pair, index) => (
                <div key={index} className="bg-leaf px-4 py-3">
                  <div className="mb-1 font-mono text-[11px] text-ink-quiet">Chord {index + 1}</div>
                  <Row label="RK" names={pair.rkNames} />
                  <Row label="ours" names={pair.oursNames} />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {row.problems.length > 0 ? (
          <ul className="space-y-1 rounded-xl border border-departure bg-departure/40 p-4 text-xs leading-relaxed text-departure-deep">
            {row.problems.map((problem, index) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
        ) : null}

        <p className="max-w-2xl text-xs leading-relaxed text-ink-faded">
          A good verdict on what comes out proves nothing — the search optimises exactly what the
          verifier measures. The drift is the number that means something.
        </p>
      </div>
    </Board>
  );
}
